import React, {useEffect} from 'react';

type UseOutsideClickParams = {
    refs: Array<React.RefObject<HTMLElement | null>>; // List of refs to listen for outside clicks
    callback: (event: MouseEvent | TouchEvent) => void; // Action to trigger on outside click
    excludeRefs?: Array<React.RefObject<HTMLElement | null>>; // Refs to exclude from triggering the callback
};

/**
 * useOutsideClick
 * A reusable hook to detect clicks outside of specified refs.
 */
export const useOutsideClick = ({ refs, callback, excludeRefs = [] }: UseOutsideClickParams) => {
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent | TouchEvent) => {

            const isExcluded = excludeRefs.some((excludeRef) => {
                return excludeRef.current && excludeRef.current.contains(event.target as Node);
            });

            if (isExcluded) {
                return;
            }

            const isOutside = refs.every((ref) => {
                return ref.current && !ref.current.contains(event.target as Node);
            });

            if (isOutside) {
                callback(event);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [refs, callback, excludeRefs]);
};
